'use client';

import { c as _c } from "react/compiler-runtime";
import { Button, Chip, Skeleton } from '@common/components';
import { CustomError } from '@core/utils';
import type { ListEmergencyRoomCodeResponse, ListPolicyCodeGeocodingDto } from '@generated/unauth';
import { cloneDeep } from 'es-toolkit';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';
import { BottomNavPortal } from '@/app/_components/BottomNavPortal';
import { RegionSelect } from '@/components/client';
import type { SEARCH_PARAMS_TYPE } from '@/constants';
import { ROUTES, SEARCH_PARAMS } from '@/constants';
import { DEFAULT_LOCATION } from '@/constants/geo';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useLocation } from '@/hooks/useLocation';
import { useEmergencyRoomParams } from '../_hooks/useEmergencyRoomParams';
import { setEmergencyRoomParams } from '../_utils/setEmergencyRoomParams';
import { TimeFieldWithBottomDrawer } from './TimeFieldWithBottomDrawer';
interface EmergencyFormProps {
  metaCode: ListEmergencyRoomCodeResponse;
  regionMetaData: ListPolicyCodeGeocodingDto[];
}
const FORM_ID = 'emergency-form';
const setSelectedRegion = (selectedRegion: SEARCH_PARAMS_TYPE.SELECTED_REGION) => {
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.set(SEARCH_PARAMS.SELECTED_REGION, JSON.stringify(selectedRegion));
  window.history.replaceState({}, '', `${window.location.pathname}?${searchParams.toString()}`);
};
export const EmergencyForm = t0 => {
  const $ = _c(59);
  const {
    metaCode,
    regionMetaData
  } = t0;
  const emergencyRoomParams = useEmergencyRoomParams();
  const searchParams = useSearchParams();
  let t1;
  if ($[0] !== searchParams) {
    t1 = _temp(searchParams?.get("selectedRegion"));
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selectedRegion = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      enabledInitialFetch: true
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    isLoading: isLoadingLocation,
    data: currentLocation,
    refetch: refetchLocation
  } = useLocation(t2);
  let t3;
  let t4;
  if ($[3] !== currentLocation || $[4] !== emergencyRoomParams) {
    t3 = () => {
      if (emergencyRoomParams) {
        return;
      }
      const today = new Date();
      const hour = today.getHours();
      const isWeekend = today.getDay() === 0 || today.getDay() === 6;
      const isEmergency = hour <= 6 || 18 <= hour;
      const params_0 = {
        emergencyRoomDayType: isWeekend ? "WEEKEND" : "WEEKDAY",
        latitude: currentLocation?.latitude || DEFAULT_LOCATION.latitude,
        longitude: currentLocation?.longitude || DEFAULT_LOCATION.longitude,
        emergencyRoomYN: isEmergency,
        emergencyRoomCodeList: [],
        isRealDistance: Boolean(currentLocation),
        time: hour
      };
      setEmergencyRoomParams(params_0);
    };
    t4 = [emergencyRoomParams, currentLocation];
    $[3] = currentLocation;
    $[4] = emergencyRoomParams;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== currentLocation || $[8] !== emergencyRoomParams || $[9] !== selectedRegion) {
    t5 = () => {
      if (!currentLocation || selectedRegion) {
        return;
      }
      setSelectedRegion({
        mainRegionCode: currentLocation.codeRegion,
        subRegionCode: currentLocation.codeSubRegion
      });
      if (emergencyRoomParams && !emergencyRoomParams.isRealDistance) {
        setEmergencyRoomParams({
          ...emergencyRoomParams,
          latitude: currentLocation.latitude || DEFAULT_LOCATION.latitude,
          longitude: currentLocation.longitude || DEFAULT_LOCATION.longitude,
          isRealDistance: true
        });
      }
    };
    t6 = [currentLocation, selectedRegion, emergencyRoomParams];
    $[7] = currentLocation;
    $[8] = emergencyRoomParams;
    $[9] = selectedRegion;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  useEffect(t5, t6);
  if (!metaCode.emergencyRoomCodeList || !metaCode.emergencyRoomTimeList) {
    throw new CustomError({
      return_message: "\uC751\uAE09\uC2E4 \uCF54\uB4DC \uBAA9\uB85D\uC744 \uBD88\uB7EC\uC624\uB294 \uC911 \uC624\uB958\uAC00 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4."
    });
  }
  const {
    push
  } = useCustomRouter();
  if (!emergencyRoomParams) {
    let t7;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex flex-col"><Skeleton className="mb-10 h-24 w-94" /><Skeleton className="mb-32 h-30 w-full" /><Skeleton className="mb-10 h-24 w-full" /><Skeleton className="mb-32 h-48 w-full" /><Skeleton className="mb-10 h-24 w-full" /><Skeleton className="mb-32 h-48 w-full" /><Skeleton className="mb-10 h-24 w-full" /><Skeleton className="h-106 w-full" /></div>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  let t7;
  if ($[13] !== currentLocation?.codeSubRegion || $[14] !== emergencyRoomParams || $[15] !== metaCode.emergencyRoomCodeList || $[16] !== push || $[17] !== regionMetaData || $[18] !== selectedRegion?.subRegionCode) {
    t7 = e => {
      e.preventDefault();
      const isEditedLocation = currentLocation?.codeSubRegion !== selectedRegion?.subRegionCode;
      const resultEmergencyRoomParams = cloneDeep(emergencyRoomParams);
      if (isEditedLocation) {
        const {
          latitude,
          longitude
        } = regionMetaData.find(item => item.code === selectedRegion?.subRegionCode) ?? {};
        if (!latitude || !longitude) {
          throw new CustomError({
            return_message: "\uB300\uD45C \uC704\uCE58\uC815\uBCF4\uB97C \uAC00\uC838\uC624\uC9C0 \uBABB\uD588\uC2B5\uB2C8\uB2E4."
          });
        }
        resultEmergencyRoomParams.latitude = Number(latitude);
        resultEmergencyRoomParams.longitude = Number(longitude);
      }
      resultEmergencyRoomParams.isRealDistance = !isEditedLocation;
      if (resultEmergencyRoomParams.emergencyRoomYN) {
        delete resultEmergencyRoomParams.time;
        delete resultEmergencyRoomParams.emergencyRoomDayType;
      }
      resultEmergencyRoomParams.emergencyRoomCodeList = [...new Set(resultEmergencyRoomParams.emergencyRoomCodeList?.flatMap(value => {
        const code = metaCode.emergencyRoomCodeList?.find(item_0 => item_0.value === value);
        return code?.codeList ?? [];
      }))];
      const url = new URL(window.location.href);
      url.pathname = ROUTES.RECOMMEND_POLICY_SITUATION_EMERGENCY_HOSPITAL_LIST.pathname;
      url.searchParams.set(SEARCH_PARAMS.EMERGENCY_ROOM_PARAMS, JSON.stringify(resultEmergencyRoomParams));
      push(url.href);
    };
    $[13] = currentLocation?.codeSubRegion;
    $[14] = emergencyRoomParams;
    $[15] = metaCode.emergencyRoomCodeList;
    $[16] = push;
    $[17] = regionMetaData;
    $[18] = selectedRegion?.subRegionCode;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <dt className="flex items-center gap-4 font-500 text-Gray-900 typo-body3">병원 선택 <span className="text-B-500">(필수)</span></dt>;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== emergencyRoomParams) {
    t9 = checked => {
      if (checked) {
        setEmergencyRoomParams({
          ...emergencyRoomParams,
          emergencyRoomYN: true
        });
      }
    };
    $[21] = emergencyRoomParams;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== emergencyRoomParams.emergencyRoomYN || $[24] !== t9) {
    t10 = <Chip checked={emergencyRoomParams.emergencyRoomYN} onCheckedChange={t9}>응급실</Chip>;
    $[23] = emergencyRoomParams.emergencyRoomYN;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const t11 = !emergencyRoomParams.emergencyRoomYN;
  let t12;
  if ($[26] !== emergencyRoomParams) {
    t12 = () => {
      setEmergencyRoomParams({
        ...emergencyRoomParams,
        emergencyRoomYN: false
      });
    };
    $[26] = emergencyRoomParams;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <Chip checked={t11} onClick={t12}>일반병원</Chip>;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t13) {
    t14 = <dl className="flex flex-col gap-10">{t8}<dd className="flex flex-wrap gap-6">{t10}{t13}</dd></dl>;
    $[31] = t10;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== refetchLocation) {
    t15 = async () => {
      const {
        data
      } = await refetchLocation();
      if (!data) {
        return;
      }
      setSelectedRegion({
        mainRegionCode: data.codeRegion,
        subRegionCode: data.codeSubRegion
      });
    };
    $[34] = refetchLocation;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== isLoadingLocation || $[37] !== selectedRegion || $[38] !== t15) {
    t16 = <RegionSelect isLoading={isLoadingLocation} label={"\uB0B4\uAC00 \uC0AC\uB294 \uC9C0\uC5ED"} labelClassName="typo-body3 font-500 text-Gray-900" value={selectedRegion} onChange={_temp2} onClickGps={t15} />;
    $[36] = isLoadingLocation;
    $[37] = selectedRegion;
    $[38] = t15;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== emergencyRoomParams) {
    t17 = emergencyRoomParams.emergencyRoomYN ? null : <TimeFieldWithBottomDrawer value={[emergencyRoomParams.emergencyRoomDayType || "WEEKDAY", emergencyRoomParams.time || 8]} onChange={t18 => {
      const [emergencyRoomDayType, time] = t18;
      setEmergencyRoomParams({
        ...emergencyRoomParams,
        emergencyRoomDayType,
        time
      });
    }} />;
    $[40] = emergencyRoomParams;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <dt className="flex items-center gap-4 font-500 text-Gray-900 typo-body3">증상<span className="text-Gray-600 typo-body4">(선택)</span></dt>;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== emergencyRoomParams || $[44] !== metaCode.emergencyRoomCodeList) {
    let t20;
    if ($[46] !== emergencyRoomParams) {
      t20 = t21 => {
        const {
          value: value_0
        } = t21;
        return <Chip key={value_0} checked={emergencyRoomParams.emergencyRoomCodeList?.includes(value_0)} className="typo-body4" onCheckedChange={checked_0 => {
          let emergencyRoomCodeList = emergencyRoomParams.emergencyRoomCodeList ?? [];
          if (checked_0) {
            emergencyRoomCodeList.push(value_0);
          } else {
            emergencyRoomCodeList = emergencyRoomCodeList.filter(emergencyCode => emergencyCode !== value_0);
          }
          setEmergencyRoomParams({
            ...emergencyRoomParams,
            emergencyRoomCodeList
          });
        }}>{value_0}</Chip>;
      };
      $[46] = emergencyRoomParams;
      $[47] = t20;
    } else {
      t20 = $[47];
    }
    t19 = metaCode.emergencyRoomCodeList.map(t20);
    $[43] = emergencyRoomParams;
    $[44] = metaCode.emergencyRoomCodeList;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[48] !== t19) {
    t20 = <dl className="flex flex-col gap-10">{t18}<dd className="flex flex-wrap gap-6">{t19}</dd></dl>;
    $[48] = t19;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  const t21 = !selectedRegion || !emergencyRoomParams.emergencyRoomYN && (!emergencyRoomParams.time || !emergencyRoomParams.emergencyRoomDayType);
  let t22;
  if ($[50] !== t21) {
    t22 = <BottomNavPortal><footer className="w-full p-20"><Button className="w-full" disabled={t21} form={FORM_ID} type="submit">병원 찾기</Button></footer></BottomNavPortal>;
    $[50] = t21;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  let t23;
  if ($[52] !== t14 || $[53] !== t16 || $[54] !== t17 || $[55] !== t20 || $[56] !== t22 || $[57] !== t7) {
    t23 = <form className="flex flex-col gap-32 pb-120" id={FORM_ID} onSubmit={t7}>{t14}{t16}{t17}{t20}{t22}</form>;
    $[52] = t14;
    $[53] = t16;
    $[54] = t17;
    $[55] = t20;
    $[56] = t22;
    $[57] = t7;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  return t23;
};
function _temp(params) {
  if (!params) {
    return;
  }
  return JSON.parse(params);
}
function _temp2(selectedRegion_0) {
  setSelectedRegion({
    mainRegionCode: selectedRegion_0.mainRegion?.code,
    subRegionCode: selectedRegion_0.subRegion?.code
  });
}